import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Empty,
  Tag,
  Modal,
  Input,
  Space,
  Tooltip
} from 'antd';
import {
  ReloadOutlined,
  FileSearchOutlined,
  AuditOutlined
} from '@ant-design/icons';
import { internalAudit } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { formatDateTime } from '../../utils/format';
import CopyableText from '../../components/internalAdmin/CopyableText';

// Stage 5N-2C: internal-admin audit log viewer.
//
// Backend returns metadata already sanitised at write time, so we can
// render it as JSON without re-stripping anything. Long values
// (request_id, target_id) get a copy-on-click affordance via
// CopyableText so the operator can paste an id into a separate query.

const ACTION_TONE = {
  // login / session
  'internal_admin.login_success':           { color: 'green',  emoji: '✓' },
  'internal_admin.login_failed':            { color: 'red',    emoji: '✕' },
  'internal_admin.logout':                  { color: 'default', emoji: '↩' },
  'internal_admin.token_rejected':          { color: 'orange', emoji: '!' },
  'internal_admin.bootstrap_created':       { color: 'blue',   emoji: '+' },
  'internal_admin.bootstrap_password_reset':{ color: 'orange', emoji: '↻' },
  // account lifecycle
  'internal.account.viewed':                { color: 'blue',   emoji: '◉' },
  'internal.account.approved':              { color: 'green',  emoji: '✓' },
  'internal.account.rejected':              { color: 'red',    emoji: '✕' },
  'internal.account.suspended':             { color: 'red',    emoji: '⏸' },
  'internal.account.reactivated':           { color: 'green',  emoji: '▶' }
};

function ActionTag({ action }) {
  const tone = ACTION_TONE[action] || { color: 'default', emoji: '·' };
  return (
    <Tag color={tone.color} style={{ fontFamily: 'monospace', fontSize: 11 }}>
      <span className="mr-1">{tone.emoji}</span>
      {action}
    </Tag>
  );
}

export default function AuditLogsPage() {
  const { notifyError } = useAppNotifications();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [actionFilter, setActionFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [details, setDetails] = useState(null);

  async function load(filter) {
    setLoading(true);
    setError(null);
    try {
      const resp = await internalAudit.list({
        limit: 100,
        action: filter ? filter.trim() : undefined
      });
      setData({
        items: Array.isArray(resp.items) ? resp.items : [],
        total: typeof resp.total === 'number' ? resp.total : 0
      });
    } catch (err) {
      setError(err);
      notifyError(err, { title: 'Could not load audit logs' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(appliedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilter]);

  function applyFilter() {
    setAppliedFilter(actionFilter.trim());
  }

  function clearFilter() {
    setActionFilter('');
    setAppliedFilter('');
  }

  const columns = useMemo(
    () => [
      {
        title: 'Time',
        key: 'createdAt',
        width: 160,
        render: (_v, row) => (
          <span className="text-xs text-ink-700">{formatDateTime(row.createdAt)}</span>
        )
      },
      {
        title: 'Admin',
        key: 'admin',
        width: 220,
        ellipsis: true,
        render: (_v, row) =>
          row.adminEmail ? (
            <div className="leading-tight min-w-0">
              <p className="m-0 text-sm font-medium text-ink-800 truncate" title={row.adminDisplayName || ''}>
                {row.adminDisplayName || row.adminEmail}
              </p>
              <p className="m-0 text-[11px] text-ink-500 truncate" title={row.adminEmail}>
                {row.adminEmail}
              </p>
              {row.adminRole ? (
                <Tag color={row.adminRole === 'super_admin' ? 'red' : 'blue'} style={{ fontSize: 10, marginTop: 2 }} bordered={false}>
                  {row.adminRole}
                </Tag>
              ) : null}
            </div>
          ) : (
            <span className="text-xs text-ink-400">(deleted admin)</span>
          )
      },
      {
        title: 'Action',
        key: 'action',
        width: 240,
        render: (_v, row) => <ActionTag action={row.action} />
      },
      {
        title: 'Target',
        key: 'target',
        width: 200,
        render: (_v, row) => {
          if (!row.targetType && !row.targetId) {
            return <span className="text-ink-400">—</span>;
          }
          return (
            <div className="leading-tight min-w-0">
              {row.targetType ? (
                <p className="m-0 text-[11px] uppercase tracking-wider text-ink-500 font-semibold">
                  {row.targetType}
                </p>
              ) : null}
              {row.targetId ? <CopyableText value={row.targetId} max={14} /> : null}
            </div>
          );
        }
      },
      {
        title: 'IP',
        key: 'ipAddress',
        width: 130,
        render: (_v, row) =>
          row.ipAddress ? (
            <code className="text-[11px] text-ink-700">{row.ipAddress}</code>
          ) : (
            <span className="text-ink-400">—</span>
          )
      },
      {
        title: 'Details',
        key: 'details',
        width: 110,
        fixed: 'right',
        render: (_v, row) => (
          <Tooltip title="View metadata JSON" mouseEnterDelay={0.3}>
            <Button
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => setDetails(row)}
            >
              JSON
            </Button>
          </Tooltip>
        )
      }
    ],
    []
  );

  return (
    <div>
      <PageHeader
        title="Audit Logs"
        subtitle="Internal-admin actions in chronological order. Metadata is sanitised — no passwords, tokens, or MFA codes are stored or shown."
        actions={
          <Space>
            <Tag color="blue" className="!text-sm !px-3 !py-1">
              {data.total} {data.total === 1 ? 'event' : 'events'}
            </Tag>
            <Button icon={<ReloadOutlined />} onClick={() => load(appliedFilter)} disabled={loading}>
              Refresh
            </Button>
          </Space>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Input
          allowClear
          prefix={<AuditOutlined className="text-ink-400" />}
          placeholder="Filter by action (e.g. account.approved, login_failed)"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          onPressEnter={applyFilter}
          className="!max-w-md"
          maxLength={64}
        />
        <Button type="primary" onClick={applyFilter} disabled={loading}>
          Filter
        </Button>
        {appliedFilter ? (
          <Button onClick={clearFilter} disabled={loading}>
            Clear
          </Button>
        ) : null}
        {appliedFilter ? (
          <Tag color="blue" className="!text-xs">
            action ~ "{appliedFilter}"
          </Tag>
        ) : null}
      </div>

      {loading ? (
        <LoadingBlock tip="Loading audit logs…" />
      ) : (
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <Table
            rowKey="id"
            dataSource={data.items}
            columns={columns}
            size="small"
            pagination={false}
            scroll={{ x: 1100 }}
            tableLayout="fixed"
            locale={{
              emptyText: error ? (
                <div className="py-12 text-center text-ink-500 text-sm">Could not load audit logs.</div>
              ) : (
                <div className="py-10">
                  <Empty
                    description={
                      <div className="text-ink-700">
                        <p className="text-sm font-semibold m-0">
                          {appliedFilter ? 'No matching audit events' : 'No audit events yet'}
                        </p>
                        <p className="text-xs text-ink-500 m-0 mt-1">
                          {appliedFilter
                            ? 'Try a different action keyword or clear the filter.'
                            : 'Internal-admin actions will appear here in chronological order.'}
                        </p>
                      </div>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )
            }}
          />
        </div>
      )}

      <Modal
        open={Boolean(details)}
        title={
          <div className="flex items-center gap-2 min-w-0">
            <FileSearchOutlined className="text-brand-500" />
            <span className="font-mono text-sm">{details && details.action}</span>
          </div>
        }
        footer={[
          <Button key="close" onClick={() => setDetails(null)}>
            Close
          </Button>
        ]}
        onCancel={() => setDetails(null)}
        destroyOnHidden
        width={680}
      >
        {details ? (
          <div className="space-y-3">
            <div className="text-xs text-ink-700">
              <span className="text-ink-500">When:</span>{' '}
              {formatDateTime(details.createdAt)}
              {details.requestId ? (
                <>
                  {' · '}
                  <span className="text-ink-500">Request:</span>{' '}
                  <CopyableText value={details.requestId} max={14} />
                </>
              ) : null}
              {details.ipAddress ? (
                <>
                  {' · '}
                  <span className="text-ink-500">IP:</span>{' '}
                  <code className="text-[11px]">{details.ipAddress}</code>
                </>
              ) : null}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold m-0 mb-1">
                Metadata
              </p>
              <pre className="bg-slate-50 border border-surface-200 rounded p-3 text-[11px] text-ink-800 overflow-auto max-h-[60vh] m-0">
                {JSON.stringify(details.metadata || {}, null, 2)}
              </pre>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
